/**
 * Dashboard Tiles - Alle Kachel-Inhalte als separate Komponenten
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabs } from '@/contexts/TabContext';
import {
  Star, Clock, Sun, Sunrise, Moon, Calendar, CheckSquare,
  Inbox, Euro, Scale, Network, FileText, ArrowRight,
  AlertTriangle, ChevronRight, Sparkles, Users, Mail, QrCode, Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { gruppiereAufgaben } from '@/lib/aufgabenBaum';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Hilfsfunktion für Zeitformatierung
function formatTimeAgo(isoString) {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffStd = Math.floor(diffMs / 3600000);
  if (diffMin < 1) return 'gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min`;
  if (diffStd < 24) return `vor ${diffStd} Std`;
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

// Ampel-Punkt
function AmpelDot({ farbe, size = 'w-3 h-3' }) {
  const colors = {
    rot: 'bg-red-500 shadow-red-500/50',
    gelb: 'bg-amber-400 shadow-amber-400/50',
    gruen: 'bg-emerald-500 shadow-emerald-500/50',
  };
  return <span className={`${size} rounded-full inline-block shadow-lg ${colors[farbe] || colors.gruen}`} />;
}

// ============ FAVORITEN TILE ============
export function FavoritenTile({ data = [], nav, onRemoveFavorit }) {
  const [confirmRemove, setConfirmRemove] = useState(null);

  const handleStarClick = (e, klient) => {
    e.stopPropagation();
    setConfirmRemove(klient);
  };

  const confirmRemoveFavorit = async () => {
    if (confirmRemove && onRemoveFavorit) {
      await onRemoveFavorit(confirmRemove.id);
      setConfirmRemove(null);
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Star className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-medium text-slate-700">Favoriten-Klienten</span>
        </div>
        <p className="text-sm text-slate-400 text-center py-4">
          Noch keine Favoriten. Markieren Sie Klienten mit dem Stern-Symbol.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
          <span className="text-sm font-medium text-slate-700">Favoriten-Klienten</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => nav('/akten', 'Betreuungsakten')} className="text-xs text-violet-600">
          Alle <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {data.slice(0, 6).map((klient) => (
          <div
            key={klient.id}
            className="flex-shrink-0 w-28 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg p-2.5 text-left transition-all group relative"
            data-testid={`favorit-tile-${klient.id}`}
          >
            <button
              onClick={(e) => handleStarClick(e, klient)}
              className="absolute top-1.5 right-1.5 p-1 rounded-full hover:bg-amber-200 transition-colors opacity-0 group-hover:opacity-100"
              title="Favorit entfernen"
              data-testid={`remove-favorit-${klient.id}`}
            >
              <Star className="w-3 h-3 text-amber-700 fill-amber-600" />
            </button>
            <button
              onClick={() => nav(`/akten/${klient.id}/blick`, `${klient.name} - Blick`)}
              className="w-full text-left"
            >
              <div className="w-7 h-7 rounded-full bg-amber-200 flex items-center justify-center mb-2">
                <Star className="w-3.5 h-3.5 text-amber-700 fill-amber-600" />
              </div>
              <p className="text-xs font-medium text-slate-800 truncate">{klient.name}</p>
              <p className="text-[10px] text-slate-500 truncate">{klient.subtext || 'Keine Az.'}</p>
            </button>
          </div>
        ))}
      </div>

      {/* Bestätigungsdialog */}
      {confirmRemove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmRemove(null)}>
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-600 fill-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Favorit entfernen?</h3>
                <p className="text-sm text-slate-500">{confirmRemove.name}</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-6">
              Möchten Sie diesen Klienten wirklich aus Ihren Favoriten entfernen?
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmRemove(null)}>
                Abbrechen
              </Button>
              <Button 
                className="flex-1 bg-amber-500 hover:bg-amber-600" 
                onClick={confirmRemoveFavorit}
                data-testid="confirm-remove-favorit"
              >
                Entfernen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ ZULETZT ANGESEHEN TILE ============
export function ZuletztAngesehenTile({ data = [], nav }) {
  if (!data || data.length === 0) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">Zuletzt angesehen</span>
        </div>
        <p className="text-sm text-slate-400 text-center py-4">
          Öffnen Sie eine Betreuungsakte, um sie hier zu sehen.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-slate-400" />
        <span className="text-sm font-medium text-slate-700">Zuletzt angesehen</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {data.slice(0, 5).map((klient) => (
          <button
            key={klient.id}
            onClick={() => nav(`/akten/${klient.id}/blick`, `${klient.name} - Blick`)}
            className="flex-shrink-0 w-32 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-lg p-2.5 text-left transition-all group"
            data-testid={`zuletzt-tile-${klient.id}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 text-[10px] font-semibold">
                  {klient.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </span>
              </div>
              <Sparkles className="w-3.5 h-3.5 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
            </div>
            <p className="text-xs font-medium text-slate-800 truncate">{klient.name}</p>
            <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-1">
              <Clock className="w-2.5 h-2.5" />
              {formatTimeAgo(klient.angesehen_um)}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============ BRIEFING TILE ============
export function BriefingTile({ data, user, nav }) {
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return { text: 'Guten Morgen', icon: Sunrise };
    if (h < 18) return { text: 'Guten Tag', icon: Sun };
    return { text: 'Guten Abend', icon: Moon };
  })();
  const GreetIcon = greeting.icon;

  const items = [
    { label: 'Fristen überfällig', count: data?.fristen?.rot || 0, color: 'text-red-600', bg: 'bg-red-50 border-red-200', icon: AlertTriangle },
    { label: 'Fristen bald fällig', count: data?.fristen?.gelb || 0, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: Clock },
    { label: 'Aufgaben offen', count: data?.aufgaben || 0, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', icon: CheckSquare },
    { label: 'Post ungelesen', count: data?.post || 0, color: 'text-violet-600', bg: 'bg-violet-50 border-violet-200', icon: Mail, route: '/posteingang', routeTitle: 'Posteingang' },
    { label: 'Schulden überfällig', count: data?.schulden || 0, color: 'text-rose-600', bg: 'bg-rose-50 border-rose-200', icon: Scale },
  ];

  return (
    <div className="p-4">
      <div className="flex items-center gap-3 mb-4">
        <GreetIcon className="w-6 h-6 text-amber-500" />
        <div>
          <h2 className="text-lg font-bold text-slate-800">{greeting.text}, {user?.vorname || 'Betreuer'}</h2>
          <p className="text-xs text-slate-500">
            {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
            {data?.klienten ? ` — ${data.klienten} aktive Betreuungen` : ''}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {items.map(item => {
          const klickbar = Boolean(item.route && nav);
          const Wrapper = klickbar ? 'button' : 'div';
          return (
          <Wrapper
            key={item.label}
            type={klickbar ? 'button' : undefined}
            onClick={klickbar ? () => nav(item.route, item.routeTitle) : undefined}
            className={`rounded-lg border px-3 py-2 text-left w-full ${item.bg} ${item.count > 0 ? 'ring-1 ring-inset' : 'opacity-70'} ${klickbar ? 'cursor-pointer hover:brightness-95 transition' : ''}`}
          >
            <div className="flex items-center gap-1.5">
              <item.icon className={`w-3.5 h-3.5 ${item.color}`} />
              <span className={`text-xl font-bold ${item.color}`}>{item.count}</span>
            </div>
            <p className="text-[10px] text-slate-600 mt-0.5">{item.label}</p>
          </Wrapper>
          );
        })}
      </div>
    </div>
  );
}

// ============ FRISTEN TILE ============
export function FristenTile({ data = { dringende: [], naechste: [], zusammenfassung: {} }, nav, compact = false }) {
  const [showFristenListe, setShowFristenListe] = useState(false);
  const [selectedFrist, setSelectedFrist] = useState(null);
  
  const zs = data.zusammenfassung || {};
  const allFristen = [...(data.dringende || []), ...(data.naechste || [])];

  const handleFristClick = (frist) => {
    setSelectedFrist(frist);
    setShowFristenListe(true);
  };

  const handleOpenAufgabe = (frist) => {
    // Direkt zur Aufgabe navigieren falls vorhanden
    if (frist.aufgabe_id) {
      nav(`/aufgaben/${frist.aufgabe_id}`, `Aufgabe: ${frist.titel || frist.typ}`);
    } else {
      nav(`/akten/${frist.klient_id}?tab=aufgaben`, frist.klient_name);
    }
    setShowFristenListe(false);
  };

  return (
    <>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="w-4 h-4 text-amber-500" /> Fristen
            <div className="flex items-center gap-1 ml-2">
              {(zs.rot || 0) > 0 && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{zs.rot}</Badge>}
              {(zs.gelb || 0) > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">{zs.gelb}</Badge>}
              <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0">{zs.gruen || 0}</Badge>
            </div>
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowFristenListe(true)} 
            className="text-xs h-7"
            data-testid="open-fristen-liste"
          >
            Übersicht <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1">
          {allFristen.slice(0, compact ? 4 : 6).map(f => (
            <div
              key={f.id}
              className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg cursor-pointer transition-colors text-sm ${
                f.ampel === 'rot' ? 'bg-red-50 hover:bg-red-100' :
                f.ampel === 'gelb' ? 'bg-amber-50 hover:bg-amber-100' :
                'bg-slate-50 hover:bg-slate-100'
              }`}
              onClick={() => handleFristClick(f)}
              data-testid={`frist-item-${f.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <AmpelDot farbe={f.ampel} size="w-2.5 h-2.5" />
                <span className="font-medium text-slate-700 truncate text-xs">{f.klient_name}</span>
                <span className="text-[10px] text-slate-500 truncate">{f.typ || f.titel}</span>
              </div>
              <span className={`text-[10px] font-bold shrink-0 ${
                f.ampel === 'rot' ? 'text-red-700' : f.ampel === 'gelb' ? 'text-amber-700' : 'text-emerald-700'
              }`}>
                {f.tage_bis < 0 ? `${Math.abs(f.tage_bis)}T über` : f.tage_bis === 0 ? 'Heute' : `${f.tage_bis}T`}
              </span>
            </div>
          ))}
          {allFristen.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-3">Keine anstehenden Fristen</p>
          )}
        </div>
      </CardContent>

      {/* Fristen-Liste Modal */}
      {showFristenListe && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowFristenListe(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="font-bold text-lg text-slate-800">Fristen-Übersicht</h2>
                  <p className="text-sm text-slate-500">
                    {allFristen.length} Fristen • {zs.rot || 0} überfällig, {zs.gelb || 0} bald fällig
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowFristenListe(false)}
                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Fristen-Liste */}
            <div className="overflow-y-auto max-h-[60vh] p-4">
              {allFristen.length === 0 ? (
                <p className="text-center text-slate-400 py-8">Keine Fristen vorhanden</p>
              ) : (
                <div className="space-y-2">
                  {allFristen.map(f => (
                    <div
                      key={f.id}
                      className={`p-4 rounded-lg border transition-all hover:shadow-md ${
                        f.ampel === 'rot' ? 'bg-red-50 border-red-200' :
                        f.ampel === 'gelb' ? 'bg-amber-50 border-amber-200' :
                        'bg-white border-slate-200'
                      }`}
                      data-testid={`fristen-liste-item-${f.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <AmpelDot farbe={f.ampel} size="w-3 h-3" />
                            <span className="font-semibold text-slate-800">{f.klient_name}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                              f.ampel === 'rot' ? 'bg-red-200 text-red-800' :
                              f.ampel === 'gelb' ? 'bg-amber-200 text-amber-800' :
                              'bg-emerald-200 text-emerald-800'
                            }`}>
                              {f.tage_bis < 0 ? `${Math.abs(f.tage_bis)} Tage überfällig` : 
                               f.tage_bis === 0 ? 'Heute fällig' : 
                               `Noch ${f.tage_bis} Tage`}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700 mb-1">{f.typ || f.titel}</p>
                          {f.beschreibung && (
                            <p className="text-xs text-slate-500">{f.beschreibung}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            Fällig am: {new Date(f.faellig_am).toLocaleDateString('de-DE', { 
                              weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' 
                            })}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => nav(`/akten/${f.klient_id}/blick`, f.klient_name)}
                            className="text-xs"
                          >
                            Zur Akte
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={() => handleOpenAufgabe(f)}
                            className="text-xs bg-violet-600 hover:bg-violet-700"
                            data-testid={`open-aufgabe-${f.id}`}
                          >
                            Aufgabe öffnen
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-slate-50 flex justify-between">
              <Button variant="outline" onClick={() => nav('/fristen', 'Fristkalender')}>
                Zum Fristkalender
              </Button>
              <Button onClick={() => setShowFristenListe(false)}>
                Schließen
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============ AUFGABEN TILE ============
export function AufgabenTile({ data = [], nav, compact = false }) {
  // Unteraufgaben zaehlen unter ihrer Hauptaufgabe (keine Aufgabenflut aus Checklisten)
  const offene = gruppiereAufgaben(data.filter(a => a.status !== 'erledigt')).map(e => e.aufgabe);
  const dringende = offene.filter(a => a.prioritaet === 'hoch' || a.prioritaet === 'dringend');
  const ueberfaellige = offene.filter(a => a.faellig_am && new Date(a.faellig_am) < new Date());

  return (
    <>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <CheckSquare className="w-4 h-4 text-blue-500" /> Offene Aufgaben
            {dringende.length > 0 && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{dringende.length} dringend</Badge>}
            {ueberfaellige.length > 0 && <Badge className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0">{ueberfaellige.length} überfällig</Badge>}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => nav('/aufgaben', 'Aufgaben')} className="text-xs text-violet-600 h-7">
            Alle ({offene.length}) <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1">
          {offene.slice(0, compact ? 4 : 6).map(a => (
            <div
              key={a.id}
              className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
              onClick={() => nav(`/aufgaben/${a.id}`, a.titel)}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-2 h-2 rounded-full shrink-0 ${
                  a.prioritaet === 'dringend' ? 'bg-red-500' :
                  a.prioritaet === 'hoch' ? 'bg-orange-500' : 'bg-blue-400'
                }`} />
                <span className="text-xs font-medium text-slate-700 truncate">{a.titel}</span>
              </div>
              {a.faellig_am && (
                <span className="text-[10px] text-slate-500 shrink-0">
                  {new Date(a.faellig_am).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                </span>
              )}
            </div>
          ))}
          {offene.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-3">Keine offenen Aufgaben</p>
          )}
        </div>
      </CardContent>
    </>
  );
}

// ============ POSTEINGANG TILE ============
export function PosteingangTile({ data = [], nav, compact = false }) {
  const ungelesen = data.filter(p => !p.gelesen && p.status !== 'bearbeitet');

  return (
    <>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Inbox className="w-4 h-4 text-violet-500" /> Posteingang
            {ungelesen.length > 0 && <Badge className="bg-violet-100 text-violet-700 text-[10px] px-1.5 py-0">{ungelesen.length} neu</Badge>}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => nav('/posteingang', 'Posteingang')} className="text-xs text-violet-600 h-7">
            Alle <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1">
          {ungelesen.slice(0, compact ? 3 : 5).map(p => (
            <div
              key={p.id}
              className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-violet-50 hover:bg-violet-100 cursor-pointer transition-colors"
              onClick={() => nav('/posteingang', 'Posteingang')}
            >
              <div className="min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">{p.titel || 'Ohne Titel'}</p>
                <p className="text-[10px] text-slate-500 truncate">{p.absender || 'Unbekannt'}</p>
              </div>
              <span className="text-[10px] text-slate-400 shrink-0">
                {p.datum ? new Date(p.datum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : ''}
              </span>
            </div>
          ))}
          {ungelesen.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-3">Keine ungelesene Post</p>
          )}
        </div>
      </CardContent>
    </>
  );
}

// ============ VERGÜTUNG TILE ============
export function VerguetungTile({ data, nav, compact = false }) {
  return (
    <>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Euro className="w-4 h-4 text-emerald-500" /> Vergütung
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => nav('/verguetung', 'Vergütung')} className="text-xs text-violet-600 h-7">
            Details <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {data ? (
          <div className="space-y-2">
            <div className="flex justify-between items-center p-2 bg-emerald-50 rounded-lg">
              <span className="text-xs text-slate-600">Aktuelles Quartal</span>
              <span className="text-sm font-bold text-emerald-700">{(data.quartal_summe || 0).toLocaleString('de-DE')} €</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-2 bg-slate-50 rounded">
                <p className="text-slate-500">Aktive Fälle</p>
                <p className="font-medium text-slate-700">{data.aktive_faelle || 0}</p>
              </div>
              <div className="p-2 bg-slate-50 rounded">
                <p className="text-slate-500">Jahr gesamt</p>
                <p className="font-medium text-slate-700">{(data.jahr_summe || 0).toLocaleString('de-DE')} €</p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-400 text-center py-3">Keine Vergütungsdaten</p>
        )}
      </CardContent>
    </>
  );
}

// ============ SCHULDEN TILE ============
export function SchuldenTile({ data, nav, compact = false }) {
  return (
    <>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Scale className="w-4 h-4 text-rose-500" /> Schulden-Übersicht
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => nav('/schulden-dashboard', 'Schulden-Dashboard')} className="text-xs text-violet-600 h-7">
            Dashboard <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {data ? (
          <div className="space-y-2">
            <div className="flex justify-between items-center p-2 bg-rose-50 rounded-lg">
              <span className="text-xs text-slate-600">Gesamtschulden</span>
              <span className="text-sm font-bold text-rose-700">{(data.gesamt_schulden || 0).toLocaleString('de-DE')} €</span>
            </div>
            {data.ueberfaellig?.anzahl > 0 && (
              <div className="p-2 bg-red-50 rounded-lg border border-red-200">
                <p className="text-[10px] text-red-600 font-medium">{data.ueberfaellig.anzahl} überfällige Zahlungen</p>
                <p className="text-xs font-bold text-red-700">{(data.ueberfaellig.summe || 0).toLocaleString('de-DE')} €</p>
              </div>
            )}
            {data.top_glaeubiger?.slice(0, 2).map((g, i) => (
              <div key={i} className="flex justify-between text-xs p-1.5 bg-slate-50 rounded">
                <span className="text-slate-600 truncate">{g.name}</span>
                <span className="font-medium text-slate-700">{(g.summe || 0).toLocaleString('de-DE')} €</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400 text-center py-3">Keine Schuldendaten</p>
        )}
      </CardContent>
    </>
  );
}

// ============ NETZWERK TILE ============
export function NetzwerkTile({ data, nav }) {
  return (
    <>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Network className="w-4 h-4 text-teal-500" /> Netzwerk
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => nav('/netzwerk', 'Netzwerk')} className="text-xs text-violet-600 h-7">
            Öffnen <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => nav('/netzwerk?tab=suche&typ=klient', 'Netzwerk')}
            className="p-2.5 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-center"
          >
            <Users className="w-5 h-5 text-blue-600 mx-auto mb-1" />
            <p className="text-[10px] text-slate-600">Betreute</p>
            <p className="text-xs font-bold text-blue-700">{data?.betreute || 0}</p>
          </button>
          <button
            onClick={() => nav('/netzwerk?tab=suche&typ=institution', 'Netzwerk')}
            className="p-2.5 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors text-center"
          >
            <Network className="w-5 h-5 text-teal-600 mx-auto mb-1" />
            <p className="text-[10px] text-slate-600">Institutionen</p>
            <p className="text-xs font-bold text-teal-700">{data?.institutionen || 0}</p>
          </button>
          <button
            onClick={() => nav('/netzwerk?tab=suche&typ=person', 'Netzwerk')}
            className="p-2.5 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-center"
          >
            <Users className="w-5 h-5 text-purple-600 mx-auto mb-1" />
            <p className="text-[10px] text-slate-600">Personen</p>
            <p className="text-xs font-bold text-purple-700">{data?.personen || 0}</p>
          </button>
        </div>
      </CardContent>
    </>
  );
}

// ============ WOCHENBERICHT TILE ============
export function WochenberichtTile({ data = [], nav }) {
  // Erledigte Aufgaben dieser Woche
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay() + 1);
  weekStart.setHours(0, 0, 0, 0);
  
  const erledigteWoche = data.filter(a => {
    if (a.status !== 'erledigt' || !a.erledigt_am) return false;
    const erledigt = new Date(a.erledigt_am);
    return erledigt >= weekStart;
  });

  return (
    <>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-500" /> Wochenbericht
            <Badge className="bg-indigo-100 text-indigo-700 text-[10px] px-1.5 py-0">{erledigteWoche.length} erledigt</Badge>
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {erledigteWoche.length > 0 ? (
          <div className="space-y-1">
            {erledigteWoche.slice(0, 5).map(a => (
              <div key={a.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-indigo-50">
                <CheckSquare className="w-3.5 h-3.5 text-indigo-500" />
                <span className="text-xs text-slate-700 truncate">{a.titel}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400 text-center py-3">Diese Woche noch keine Aufgaben erledigt</p>
        )}
      </CardContent>
    </>
  );
}


// ============ QR-ÜBERWEISUNG TILE ============
export function QRUeberweisungTile({ nav }) {
  return (
    <>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <QrCode className="w-4 h-4 text-violet-500" /> QR-Code Generierung
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="text-center py-4">
          <button
            onClick={() => nav('/qr-ueberweisung', 'QR-Code Generierung')}
            className="w-full p-4 rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 hover:border-violet-300 hover:shadow-md transition-all group"
            data-testid="qr-tile-btn"
          >
            <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-violet-200 transition-colors">
              <QrCode className="w-6 h-6 text-violet-600" />
            </div>
            <p className="font-medium text-slate-800 mb-1">Beleg scannen</p>
            <p className="text-xs text-slate-500">
              Rechnung hochladen → QR-Code für Banking-App
            </p>
          </button>
          <div className="flex items-center justify-center gap-2 mt-3 text-xs text-slate-400">
            <Upload className="w-3.5 h-3.5" />
            PDF, PNG, JPG unterstützt
          </div>
        </div>
      </CardContent>
    </>
  );
}
